import type { Messages } from '../messages.js'

/**
 * Latvian. `gājiens` for flips, the word a Latvian board game uses for a move.
 *
 * Three plural forms, and the odd one is `zero`: Latvian uses it for every number ending in
 * nought, so 10, 20 and 130 all take the same form as 0. It is a genitive plural, not an
 * absence — `20 vārdu`.
 */
export const lv: Messages = {
  tag: 'lv',

  readingDictionary: 'Lasa vārdnīcu…',
  noWordList: 'Nav vārdu saraksta valodai “{language}”. Izveidojiet to:  pnpm dictionary build',
  emptyWordList: 'Vārdu saraksts valodai “{language}” ir tukšs.',

  flips: 'gājieni',
  score: 'punkti',
  words: 'vārdi',
  round: 'raunds',
  ticksLeftLabel: 'Atlikušais laiks šajā raundā',
  typeAWord: 'ierakstiet vārdu',
  tapPrompt: 'pieskarieties burtiem, lai izvēlētos vai atdotu, tad {action}',

  boardOfTiles: 'Galdiņš ar {n} kauliņiem',
  faceDown: 'apgriezts',
  wildCard: 'džokers',
  wildKey: 'jebkurš burts',
  letterReplaced: '{from} kļuva par {to}',
  letterSwap: 'BURTU MAIŅA!',
  spentTile: 'izmantots kauliņš',
  hiddenWhilePaused: 'paslēpts pauzes laikā',
  letterInWord: '{letter}, vārda {position}. burts',

  completeWord: 'Pabeigt vārdu',

  completeShort: 'Pabeigt',
  reset: 'Notīrīt',
  pause: 'Pauze',
  resume: 'Turpināt',
  newGame: 'Jauna spēle',
  paused: 'Apturēts',
  outOfFlips: 'Gājieni beigušies',
  finalResult: '{score} par {words} {rounds} laikā',
  playAgain: 'Spēlēt vēlreiz',
  share: 'Kopīgot',
  shareCopied: 'Nokopēts.',
  shareSelect: 'Nokopējiet šo:',

  lettersSelect: 'burti izvēlas',
  keysWild: 'tiek paņemts, kad ierakstāt burtu, kura nav uz galdiņa',
  clearsEvery: 'notīra visus izvēlētos {letter}',
  undoLastLetter: 'atsaukt pēdējo burtu',
  noWordsYet: 'Vēl neviena vārda.',

  wordRejected: '{word}  {reason}',
  reasonDuplicate: 'jau atrasts',
  reasonTooShort: 'pārāk īss',
  reasonNotAWord: 'tas nav vārds',
  reasonAllFound: 'jums jau ir tie visi',
  noSuchLetterUp: 'nav atklāta {letter}',
  nothingUp: 'nekas nav atklāts',
  letterHidden: 'burts paslēpās',
  shuffled: 'sajaukts',
  shuffledAndBilled: 'sajaukts, ieskaitīti {flips} neizmantoti',
  tooFewLetters: 'Palicis pārāk maz burtu',

  gameLanguage: 'valoda',
  interfaceLanguage: 'saskarne',
  dictionarySize: '{common} ikdienas no {full} vārdiem',
  filterLanguages: 'Meklēt valodu',
  noMatches: 'Nav atbilstību',

  nerdMode: 'zinātāju režīms',
  rules: 'Noteikumi',
  difficulty: 'grūtība',
  difficultyNames: { easy: 'viegla', medium: 'vidēja', hard: 'grūta', insane: 'trakā' },
  pausedNoBoard: 'Šī spēle rezultātu tabulā neparādīsies.',
  about: 'Par',
  themeLabel: 'tēma',
  themeNames: { traditional: 'tradicionāla', light: 'gaiša', contrast: 'augsts kontrasts' },
  tiles: 'kauliņi (N)',
  secondsPerTick: 'sekundes / takts',
  holdTicks: 'noturēšanas taktis',
  minWord: 'īsākais vārds',
  startingFlips: 'sākuma gājieni',
  wildChance: 'džokera iespēja',
  replaceChance: 'burtu maiņas iespēja',
  hideChance: 'burta paslēpšanas iespēja',
  wordCompleteMode: 'kad vārds pabeigts',
  wordCompleteNames: { shuffle: 'sajaukt', spend: 'izlietot', keep: 'paturēt' },
  flipEconomy: 'gājienu atdeve',
  flipEconomyNames: {
    none: 'nav',
    perLetter: 'par burtu',
    fibonacci: 'fibonači',
    overMinimum: 'virs minimuma',
  },
  repeatedLetterKey: 'atkārtota burta taustiņš',
  keySchemeNames: { cycle: 'ciklis', advance: 'uz priekšu' },
  keySchemeHelp: {
    cycle:
      'A paņem nākamo neizmantoto A, un, kad tie visi ir vārdā, notīra tos. ' +
      'Shift+A tos arī notīra.',
    advance: 'A paņem nākamo neizmantoto A. Shift+A notīra no vārda visus A.',
  },

  whatThatMeans: 'Ko tas nozīmē',
  factRound: 'raunds',
  factWholeBoardUp: 'viss galdiņš atklāts',
  factRoundCosts: 'raunds maksā',
  factFlipsBuy: 'sākuma gājieni nopērk',
  factThisBoard: 'šis galdiņš',
  factBoardHadToAdmit: 'galdiņam bija jāatzīst',
  ticksAndSeconds: '{ticks}, {seconds}',
  wordsLongest: '{words}, garākais {longest}',
  wordsIncludingOneOf: '{words}, no tiem viens no {ceiling}',
  scorelessRounds: '{rounds} bez punktiem',

  whatAWordPays: 'Ko dod vārds',
  columnLetters: 'burti',
  columnCost: 'cena',
  columnPoints: 'punkti',
  columnFlips: 'gājieni',
  columnNet: 'starpība',

  canonicalRules: 'Parastie {difficulty} noteikumi.',
  customRules: 'Mainīts no iepriekšiestatītā. Rezultāti pēc saviem noteikumiem netiek vērtēti.',
  applyAndStart: 'Lietot un sākt jaunu spēli',
  changesNextGame: 'Izmaiņas stāsies spēkā nākamajā spēlē.',
  presets: 'Iestatījumi:',

  start: 'Sākt',
  restart: 'No jauna',
  quit: 'Beigt',
  quitTitle: 'Vai beigt šo spēli?',
  restartTitle: 'Vai sākt šo spēli no jauna?',
  restartConfirm: 'No jauna',
  quitConfirm: 'Beigt',
  keepPlaying: 'Spēlēt tālāk',
  personalBest: 'Jūsu labākās spēles',
  newPersonalBest: 'Jauns personiskais rekords.',
  columnRank: '#',
  notRanked: 'Savi noteikumi, tāpēc šī spēle netiek vērtēta.',
  notRankedPaused: 'Laiks apstājās, tāpēc šī spēle netiek vērtēta.',
  rankOfTotal: '{rank} no {total}',

  howToPlay: 'Kā spēlēt',

  backToGame: 'Atpakaļ uz spēli',
  welcomeTitle: 'Laipni lūdzam Blinkered',
  tutorialSkip: 'Izlaist',
  tutorialNext: 'Tālāk',
  tutorialBack: 'Atpakaļ',
  tutorialStart: 'Sākt spēlēt',
  tutorialHideAgain: 'Vairs nerādīt',
  tutorialProgress: '{n} no {total}',
  tutorialSkipTitle: 'Vai izlaist ievadu?',
  tutGoalTitle: 'Blinkered ir sacīkstes',
  tutGoalBody:
    'Spēli jūs sākat ar noteiktu gājienu skaitu, un katrs atklātais kauliņš maksā vienu gājienu. Kad gājieni beidzas, spēle ir galā. Vārdi nes gājienus, un jo garāks vārds, jo vairāk gājienu jūs nopelnāt.',
  tutSpentBody:
    'Vārdā izmantotie burti paliek apgriezti līdz nākamajam raundam. Tikai burta nomaiņa maina, kuri burti ir uz galdiņa.',
  tutChangeTitle: 'Burti var mainīties',
  tutSameLetters:
    'Pamanījāt vārdu, ko nepaspējāt izspēlēt? Tie paši burti atgriežas nākamajā raundā, sajaukti jaunās vietās.',
  tutPickLetters: 'Pieskarieties vēlamajiem burtiem pēc kārtas, lai izveidotu vārdu.',
  tutMoreTurn: 'Kauliņi turpina griezties, kamēr domājat, tāpēc labāks burts vēl var pienākt.',
  tutTapBack:
    'Paņēmāt burtu, kuru negribējāt? Pieskarieties tam vēlreiz, un tas atgriezīsies. Jebkuram, ne tikai pēdējam.',
  tutComplete: 'Kad vārds gatavs, nospiediet Pabeigt.',
  htBoardTitle: 'Galdiņš',
  htBoardBody: 'Kauliņi atklājas pa vienam, nejaušā secībā. No atklātajiem veido vārdus.',
  htWordsTitle: 'Vārdi',
  htWordsBody:
    'Izveidojiet vārdu no atklātajiem kauliņiem, rakstot vai klikšķinot burtus pēc kārtas.',
  htFlipsTitle: 'Gājieni',
  htFlipsBody:
    'Katrs atklātais kauliņš maksā gājienu. Pabeigts vārds atdod gājienus, un garāki vārdi atdod vairāk. Kad gājieni beidzas, spēle ir galā.',
  htRoundTitle: 'Raunds',
  htRoundBody:
    'Kad atklājas raunda pēdējais kauliņš, viss galdiņš uz mirkli paliek. Tad kauliņus apgriež un sajauc, un sākas jauns raunds.',
  htLanguagesTitle: 'Valodas',
  htLanguagesBody:
    'To ir {n}. Katru galdiņu var atrisināt ar vārdiem, ko cilvēki tiešām lieto. Rets vārds arī dod punktus, ja vārdnīca to zina.',
  htKeysTitle: 'Tastatūra',
  htWildTitle: 'Džokeri',
  htWildBody:
    'Reizēm burta vietā parādās džokers. Džokers skaitās par jebkuru burtu, kas veido derīgu vārdu. Jau atrasts vārds neskaitās.',
  htHideTitle: 'Burti, kas paslēpjas',
  htHideBody:
    'Grūtākajos līmeņos atklāts burts var pats apgriezties un vēlāk tajā pašā kārtā atgriezties. Taimeris par to saņem papildu atzīmi, sarkanā krāsā. Jau izvēlētie burti neapgriežas.',
  tutHideBody:
    'Grūtākajos līmeņos atklāts burts var pats apgriezties un vēlāk tajā pašā kārtā atgriezties. Jau izvēlētie burti neapgriežas.',
  htSwapTitle: 'Mainīgie burti',
  htSwapBody:
    'Reizēm starp raundiem viens burts tiek nomainīts pret citu. Redzēsiet, kurš pazuda un kurš pienāca.',
  htLevelsTitle: 'Līmeņi',
  htLevelEasy:
    'Tie paši divpadsmit burti visu spēli, tāpēc tos var iemācīties un nēsāt vārdu sarakstu galvā. Kauliņi atklājas lēni, un viss galdiņš paliek redzams pietiekami ilgi, lai pabeigtu izvēli.',
  htLevelMedium:
    'Ik pa laikam burts mainās, tāpēc grūtāk atcerēties vēlākam laikam atliktos vārdus. Mazāk laika skatīties un mazāk domāt.',
  htLevelHard:
    'Trīsburtu vārdi vairs neskaitās, un burts mainās aptuveni katru otro raundu. Galdiņš tikko paguvis parādīties, kad jau tiek sajaukts.',
  htLevelInsane:
    'Viss uzreiz un pilnā ātrumā. Galdiņš tiek sajaukts gandrīz tūlīt pēc pēdējā gājiena.',
  htTouchTitle: 'Skārienekrāns',
  htTouchBody:
    'Pieskarieties atklātam kauliņam, lai paņemtu tā burtu. Pieskarieties paņemtam burtam, lai to atdotu. Pabeigt un Notīrīt ir zem galdiņa.',

  accountTitle: 'Tavs konts',
  menuProfile: 'Mans profils',
  menuGames: 'Manas spēles',
  menuPublicPage: 'Mana publiskā lapa',
  menuSignedInAs: 'Pieteicies kā',
  signIn: 'Pieteikties',
  signOut: 'Iziet',
  signInTitle: 'Piesakies vai izveido kontu',
  signInLead: 'Konts saglabā tavas spēles, visās ierīcēs.',
  signInKeepGame: 'Piesakies, lai šī spēle paliktu, un visas nākamās.',
  continueWithApple: 'Turpināt ar Apple',
  continueWithGoogle: 'Turpināt ar Google',
  signInOr: 'vai',
  emailLabel: 'E-pasts',
  codeLabel: 'Kods',
  emailMeACode: 'Atsūtiet man kodu',
  working: 'Mirkli…',
  sendNewCode: 'Sūtīt jaunu kodu',
  codeSent: 'Paskaties e-pastā. Kods der 10 minūtes.',
  notNow: 'Ne tagad',
  badEmail: 'Tas neizskatās pēc e-pasta adreses.',
  badCode: 'Šis kods nederēja. Kodi zaudē spēku pēc 10 minūtēm un der vienreiz.',
  serverBusy: 'Neizdevās sasniegt serveri. Pamēģini pēc mirkļa.',
  serverDown: 'Neizdevās sasniegt serveri.',
  offline: 'Bezsaistē',
  leaderboardTitle: 'Rezultātu tabula',
  leaderboardEmpty: 'Šajā tabulā vēl nav spēļu. Esi pirmais.',
  leaderboardWouldBe: 'Kur būtu šī spēle',
  leaderboardThisGame: 'Šī spēle',
  leaderboardRanksHere: 'Kur ir šī spēle',
  leaderboardOutside: 'Ārpus pirmā piecnieka.',
  tutAccountTitle: 'Saglabā savus rezultātus',
  tutAccountBody: 'Konts saglabā tavas spēles, ieliek rezultātus tabulā un uztur tavu profilu.',
  ssoCancelled: 'Pieteikšanās atcelta.',
  ssoExpired: 'Tas ilga par ilgu. Piesakies vēlreiz.',
  ssoNoUsername: 'Neizdevās pabeigt konta izveidi. Pamēģini pēc mirkļa.',
  ssoFailed: 'Neizdevās. Pamēģini vēlreiz vai izmanto e-pastu.',
  tabProfile: 'Profils',
  countryAny: 'Labāk neteikšu',
  findCountry: 'Meklēt valstis',
  usernameLabel: 'Lietotājvārds',
  bioLabel: 'Par mani',
  countryLabel: 'Valsts',
  bioHint: 'Bez saitēm. {left}/{max}',
  save: 'Saglabāt',
  saving: 'Saglabā…',
  saved: 'Saglabāts.',
  nameTaken: 'Tāds vārds kādam jau ir.',
  nameTooShort: 'Mazliet garāku, lūdzu.',
  nameTooLong: 'Par garu.',
  nameBadCharacters: 'Tikai burti, cipari, defises un apakšsvītras.',
  nameBadEdges: 'Jāsākas un jābeidzas ar burtu vai ciparu.',
  nameMixedScripts: 'Izvēlies vienu alfabētu un paliec pie tā.',
  nameReserved: 'Izskatās pēc vārda, ko izdalām mēs paši. Izvēlies citu.',
  nameUnusable: 'Šo vārdu nevar izmantot.',
  bioTooLong: 'Šeit ietilpst ne vairāk kā {max} rakstzīmes.',
  bioHasLink: 'Saites šeit nav atļautas.',
  bioHasControl: 'Ir rakstzīmes, kas šeit neietilpst.',
  bioUnusable: 'Šo tekstu nevar izmantot.',
  gamesHeading: 'Spēles',
  gamesEmpty: 'Šeit vēl nekā nav. Spēles, ko nospēlēsi pieteicies, parādīsies šeit.',
  gamesEmptyShort: 'Šeit vēl nekā nav.',
  gamesLoading: 'Lasa tavas spēles…',
  theirGamesLoading: 'Lasa viņu spēles…',
  profileLoading: 'Lasa profilu…',
  playerNotFound: 'Šeit nav neviena ar tādu vārdu.',
  columnWhen: 'Kad',
  columnRounds: 'raundi',
  columnGame: 'Spēle',
  gameLoading: 'Lasa spēli…',
  gameUnreadable: 'Šo spēli neizdevās izlasīt.',
  gameNotRanked: 'Spēlēts ar mainītiem noteikumiem, tāpēc netiek vērtēts.',
  detailNotKept: 'Šīs spēles vārdi un laukumi vairs netiek glabāti.',
  openingBoard: 'Sākuma laukums',
  roundByRound: 'Kārtu pa kārtai',
  roundNothing: 'nekas netika atrasts',
  changeReplaced: '{letter} nomainīts {slot}. vietā',
  changeWildOn: '{letter} džokers {slot}. vietā',
  changeWildOff: '{letter} džokers pazuda no {slot}. vietas',
  keepThisGame: 'Saglabāt šo spēli',
  savedToYourGames: 'Saglabāts tavās spēlēs.',

  reportThis: 'Ziņot',
  reportHeading: 'Ziņot par šo',
  reportTheName: 'Vārds',
  reportTheBio: 'Apraksts',
  reportTheScore: 'Rezultāts',
  reportWhy: 'Kas ar to nav kārtībā? Pēc izvēles.',
  reportSend: 'Nosūtīt ziņojumu',
  reportSent: 'Paldies. Kāds to apskatīs.',
  reportSignIn: 'Pieraksties, lai par kaut ko ziņotu.',
  reportGone: 'Tā vairs nav.',
  reportCancel: 'Atcelt',

  deleteAccount: 'Dzēst manu kontu',
  deleteAccountWhat:
    'Tas neatgriezeniski dzēš tavu profilu un visas saglabātās spēles. To nevar atsaukt.',
  deleteAccountAsk: 'Nosūtiet man kodu',
  deleteConfirm: 'Dzēst neatgriezeniski',
  deleteGone: 'Tavs konts ir dzēsts. Paldies, ka spēlēji.',
  deleteNoAddress:
    'Šim kontam nav apstiprinātas adreses, tāpēc dzēšanu nevar pārbaudīt. Sazinies ar mums, un izdarīsim to ar roku.',

  rulesetFacts: 'Vārdi no {letters} · jauns kauliņš ik {seconds}',
  plurals: {
    words: { zero: '{n} vārdu', one: '{n} vārds', other: '{n} vārdi' },
    rounds: { zero: '{n} raundu', one: '{n} raunds', other: '{n} raundi' },
    flips: { zero: '{n} gājienu', one: '{n} gājiens', other: '{n} gājieni' },
    ticks: { zero: '{n} taktu', one: '{n} takts', other: '{n} taktis' },
    points: { zero: '{n} punktu', one: '{n} punkts', other: '{n} punkti' },
    minLetters: {
      zero: 'vismaz {n} burtiem',
      one: 'vismaz {n} burta',
      other: 'vismaz {n} burtiem',
    },
    seconds: { zero: '{n} sekundes', one: '{n} sekundi', other: '{n} sekundes' },
  },
}
